/**
 * iter 72aj — AR Detail (aging) report.
 *
 * Business-workspace only. Groups every open posted invoice by customer,
 * with per-invoice detail rows and aging buckets (Current / 1-30 / 31-60 /
 * 61-90 / 90+ days overdue). Bottom banner shows the grand aging summary.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileText, ChevronDown, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton } from "./_shared";
import { StatementDialog } from "./StatementDialog";

const BUCKETS = [
  { key: "current", label: "Current", bg: "bg-[#EAF4EE]", tx: "text-[#2F6B4A]" },
  { key: "d1_30", label: "1-30 d", bg: "bg-[#FFF7E0]", tx: "text-[#8A6B21]" },
  { key: "d31_60", label: "31-60 d", bg: "bg-[#FFEDD5]", tx: "text-[#9A4517]" },
  { key: "d61_90", label: "61-90 d", bg: "bg-[#FCE7E9]", tx: "text-[#B4283A]" },
  { key: "d90_plus", label: "90+ d", bg: "bg-[#F5D6DA]", tx: "text-[#7C0E1C]" },
];

export function ARDetailReport({ currency }) {
  const [asOf, setAsOf] = useState(new Date());
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [stmtParty, setStmtParty] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/ar-detail",
                                       { params: { as_of: fmtISO(asOf) } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load AR Detail");
    }
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[var(--muted-text)] text-sm p-6">Loading…</div>;

  return (
    <div data-testid="ar-detail-report" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <DateField label="As of" value={asOf} onChange={setAsOf} testId="ar-detail-asof" />
        <div className="flex items-center gap-2">
          <CsvButton testId="ar-detail-csv"
                      onClick={() => downloadStream("/reports/ar-detail.csv",
                                                     { as_of: fmtISO(asOf) },
                                                     `ar_detail_${fmtISO(asOf)}.csv`)} />
          <Button data-testid="ar-detail-pdf-btn" variant="outline" size="sm"
                   className="rounded-full"
                   onClick={() => downloadStream("/reports/ar-detail.pdf",
                                                  { as_of: fmtISO(asOf) },
                                                  `ar_detail_${fmtISO(asOf)}.pdf`)}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div data-testid="ar-detail-list" className="space-y-3">
        {data.customers.length === 0 && (
          <div className="card-flat p-8 text-center text-[var(--muted-text)]">
            No open invoices as of {data.as_of}. Nothing outstanding — you're all caught up.
          </div>
        )}
        {data.customers.map((c) => {
          const isOpen = expanded[c.customer_id] !== false; // default open
          return (
            <div key={c.customer_id} className="card-flat overflow-hidden"
                  data-testid={`ar-cust-${c.customer_id}`}>
              <button
                type="button"
                onClick={() => setExpanded({ ...expanded, [c.customer_id]: !isOpen })}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-[var(--divider)] hover:bg-[var(--hover)] transition text-left"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div className="font-semibold text-[var(--brand-text)]">{c.customer_name}</div>
                  <div className="text-xs text-[var(--muted-text)]">{c.invoices.length} open</div>
                </div>
                <div className="flex items-center gap-3">
                  {BUCKETS.map((b) => c.buckets[b.key] > 0.005 && (
                    <span key={b.key} className={`text-[10px] px-2 py-0.5 rounded-full ${b.bg} ${b.tx}`}>
                      {b.label}: {formatCurrency(c.buckets[b.key], currency)}
                    </span>
                  ))}
                  <div data-testid={`ar-cust-total-${c.customer_id}`}
                        className="font-mono font-semibold text-[var(--text)]">
                    {formatCurrency(c.total, currency)}
                  </div>
                  {/* iter 72ak — Statement of Account per customer. */}
                  <Button data-testid={`ar-cust-stmt-${c.customer_id}`}
                           size="sm" variant="outline"
                           className="rounded-full h-7 text-xs"
                           onClick={(e) => {
                             e.stopPropagation();
                             setStmtParty({ id: c.customer_id, name: c.customer_name, email: c.customer_email || "" });
                           }}>
                    <Send className="w-3 h-3 mr-1" /> Statement
                  </Button>
                </div>
              </button>
              {isOpen && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                      <th className="py-2 pl-8 text-left">Invoice</th>
                      <th className="py-2 text-left">Date</th>
                      <th className="py-2 text-left">Due</th>
                      <th className="py-2 text-right">Original</th>
                      <th className="py-2 text-right">Paid</th>
                      <th className="py-2 text-right">Balance</th>
                      <th className="py-2 pr-5 text-right">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.invoices.map((inv) => {
                      const bMeta = BUCKETS.find((b) => b.key === inv.bucket) || BUCKETS[0];
                      return (
                        <tr key={inv.id} data-testid={`ar-inv-${inv.id}`}
                            className="border-b border-[var(--divider)] last:border-0">
                          <td className="py-2 pl-8 font-mono text-xs">{inv.number || "—"}</td>
                          <td className="py-2 text-xs">{inv.date}</td>
                          <td className="py-2 text-xs">{inv.due_date || "—"}</td>
                          <td className="py-2 text-right font-mono text-xs">{formatCurrency(inv.original, inv.currency)}</td>
                          <td className="py-2 text-right font-mono text-xs">{formatCurrency(inv.paid, inv.currency)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatCurrency(inv.balance, inv.currency)}</td>
                          <td className="py-2 pr-5 text-right">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${bMeta.bg} ${bMeta.tx}`}>
                              {inv.days_overdue > 0 ? `${inv.days_overdue}d` : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {/* Grand aging banner */}
      <div data-testid="ar-detail-aging-banner"
            className="card-flat overflow-hidden mt-3">
        <div className="grid grid-cols-6 text-center bg-[#2F4F4F] text-white text-[10px] uppercase tracking-wider">
          {BUCKETS.map((b) => (<div key={b.key} className="py-2 border-r border-white/10">{b.label}</div>))}
          <div className="py-2 font-semibold">TOTAL</div>
        </div>
        <div className="grid grid-cols-6 text-center bg-[#EDF1EE] font-mono">
          {BUCKETS.map((b) => (
            <div key={b.key} className="py-3 border-r border-[var(--divider)]">
              {formatCurrency(data.totals.buckets[b.key], currency)}
            </div>
          ))}
          <div data-testid="ar-detail-grand-total" className="py-3 font-semibold text-[var(--brand-text)]">
            {formatCurrency(data.totals.grand_total, currency)}
          </div>
        </div>
      </div>

      <StatementDialog open={!!stmtParty} onOpenChange={(v) => !v && setStmtParty(null)}
                        kind="customer" party={stmtParty} />
    </div>
  );
}
